import { FormControl } from "@angular/forms";

import { InspectionImageSchema } from "../_offline/models/inspectionImage.schema";
import { Vertices2Df } from "./remeasurement";
import { TenantDataFile } from "./tenant-data";
import { Tags } from "exifreader";

export enum UploadOption {
  None, // 0
  Replace, // 1
  Skip, // 2
  KeepBoth // 3
}

export class BookMarkInfoWrapper {
  bookMarkInfo: BookMarkInfo;
  filePath: string;

  constructor(bookMarkInfo: BookMarkInfo, filePath: string) {
    this.bookMarkInfo = bookMarkInfo;
    this.filePath = filePath;
  }
}

export class BookMarkInfo {
  markers?: BookMarkOption[]; // Old format
  EVT?: brianMarker[]; // Brian format
}

export class BookMarkOption {
  mtime: string;
}

export class brianMarker {
  TIM: string; // Time
  TAG: string; // Tag
  STS: string; // Status
  TMP?: string; // Temporary
  NUM?: string; // Number
}

export type ExifData = { tags: Tags; fileType: string; mimeType: string };

export class AudioWrapper {
  file: File;
  filePath: string;

  constructor(file: File, filePath: string) {
    this.file = file;
    this.filePath = filePath;
  }
}

export enum ReadImageOption {
  Upload, // 0
  Sync // 1
}

export class InspectionImage {
  id: string;
  inspectionId: string;
  subjectLocationId: string;
  mimeType: string;
  originalMimeType: string;
  imageType: string;

  fileName: string;
  filePath: string;
  originalFilePath: string;
  modifiedFilePath: string;
  measurementFilePath: string;
  thumbFilePath: string;
  audioFilePath: string;
  uploadAudioUrl: string;
  originalThumbFilePath: string;
  modifiedThumbFilePath: string;
  measurementThumbFilePath: string;

  imageDescription: string;
  datetime: string;
  orientation: number;
  originalOrientation: number;
  sortOrder: number;
  comment: string; // コメント

  metadata: Metadata;
  permission: number;
  isDataPlate: boolean;
  usedImageMode: number;
  /** EXIFに計測情報が保存されている場合はTrue */
  hasMeasurementImage: boolean;
  isMeasurementEnabled: boolean;
  hasModifiedImage: boolean;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  modifiedWidth: number;
  modifiedHeight: number;
  measurementWidth: number;
  measurementHeight: number;
  status: string;

  schemaVersion: string;
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;

  scopeModel: string;
  bookMarkOption: BookMarkOption[];
}

export class InspectionImageInfo {
  imageId: string;
  inspectionId: string;
  status: string;
  uploadUrl: string;
  uploadAudioUrl?: string;
  fileName: string;
  originalFileName: string;
  folderName: string;
  comment?: string;
}

export class InspectionImageCreate {
  inspectionId: string;
  fileName: string;
  mimeType: string;
  folderName: string;
  file?: File;
  locationName: string;
  deviceFileUri: string;
  uploadOption: UploadOption;
  bookMarkOption: BookMarkOption[];
  audioFile?: File;
  isExistsAudio: boolean;
}

export class ConflictImage {
  inspectionImageCreate: InspectionImageCreate | InspectionImageUpdate; // リクエスト用
  file?: File;
  fileName: string; // Path when uploading (up to 2 levels)
  fullPath: string; // Full path locally

  originalPath: string;
  conflictPath: string;
  conflictFile: File;
  oscTenantDataFile: TenantDataFile;
  isWithoutExtension: boolean;

  constructor(
    file: File,
    fileName: string,
    fullPath: string,
    inspectionImageCreate: InspectionImageCreate | InspectionImageUpdate,
    originalPath: string,
    conflictPath: string,
    isWithoutExtension: boolean,
    conflictFile?: File,
    oscTenantDataFile?: TenantDataFile
  ) {
    this.file = file;
    this.fileName = fileName;
    this.fullPath = fullPath;

    this.inspectionImageCreate = inspectionImageCreate;

    this.originalPath = originalPath;
    this.conflictPath = conflictPath;
    this.conflictFile = conflictFile;
    this.oscTenantDataFile = oscTenantDataFile;

    this.isWithoutExtension = isWithoutExtension;
  }
}

export class InspectionImageUpdate {
  id: string;
  inspectionId: string;
  subjectLocationId: string;
  imageDescription: string;
  comment: string;
  metadata: Metadata;
  schemaVersion: string;
  uploadOption: UploadOption;
  isDataPlate?: boolean;
  bookMarkOption?: BookMarkOption[];
}

export class InspectionImageMultipleUpdate {
  imageIds: InspectionImageListItem[];
  subjectLocationId: string;
  comment: string;
  metadata: MetadataPrimary;
}

export class InspectionImageIdListItem {
  id: string;
  locationId: string;
  status: string;
}

export class InspectionImageOrderChange {
  inspectionId: string;
  subjectLocationId: string;
  imagesSortOrder: InspectionImageOrder[];

  constructor(inspectionId: string, subjectLocationId: string, imagesSortOrder: InspectionImageOrder[]) {
    this.inspectionId = inspectionId;
    this.subjectLocationId = subjectLocationId;
    this.imagesSortOrder = imagesSortOrder;
  }
}

export class InspectionImageOrder {
  id: string;
  sortOrder: number;

  constructor(id: string, sortOrder: number) {
    this.id = id;
    this.sortOrder = sortOrder;
  }
}

export class ModifiedImageUseUpdate {
  // Image ID
  id: string;
  inspectionId: string;
  usedImageMode: number;
}

/**
 * OLYMPUS拡張情報。
 */
export class Metadata {
  mark: number; // 0:× 1:A 2:B 3:C 4:D
  title: string; // 'CARCKED'など
  tags: string[];

  gain: string;
  contrast: number;
  brightness: number;
  zoom: string; // Zoom倍率。nullの場合もある。

  notes: Note[]; // ノート。要素数上限 10

  measurement: Measurement[];
  adapterName: string;
  setNumber: string;
}
export class MetadataPrimary {
  mark: number;
  title: string;
  tags: string[];
}

/**
 * ノート
 */
export class Note {
  type: number;
  point: Point; // 中心位置
  text: string;
}

/**
 * 測定
 */
export class Measurement {
  unitOfLength: string;
  data: MeasuementData[]; // 計測データ。
}

/**
 * 個別の測定データ
 */
export class MeasuementData {
  /**
   * 計測種別
   * 0 : 計測結果無し
   * 1 : IV7二点間計測, 2 : IV7線基準計測, 3 : IV7面基準計測
   * 4 : IV7全長計測, 5 : IV7面積計測
   * 10 : Vega二点間計測, 11 : Vega線基準計測, 12 : Vega面基準計測
   * 13 : Vega全長計測, 14 : Vega面積計測, 18 : Vegaスケーラ計測
   */
  type: number;
  /**
   * 計測結果
   * 面積計測の場合は、面積、
   * 自動欠損モードの場合は、欠損の幅
   * その他の計測の場合は、長さを記録する。
   */
  result: number;
  /**
   * 補助結果
   * 面積計測の場合は、長さ、自動欠損モードの場合は、欠損の面積を記録する。
   */
  subResult: number;
  /** 自動欠損結果 */
  defectLength: number;
  /** 物体距離 */
  objectDistance: number;
  /**
   * 物体距離判定結果
   * 0:緑, 1:黄, 2:赤
   */
  accuracyZ: number;
  /** オフセット線構成点の座標 */
  offsetPoints: Point[];
  /** 計測点の座標 */
  points: Point[];
  /** 基準点の座標 */
  refPoints: Point[];
}

/**
 * 座標
 */
export class Point {
  x: number;
  y: number;
}

export class InspectionImageListItem {
  id: string;
  inspectionId: string;
  subjectLocationId: string;
  mimeType: string;
  videoDuration?: number;
  datetime: string;
  fileName: string;
  thumbFilePath: string;
  imageDescription: string;
  mark: number;
  title: string;
  tags: string[];
  comment: string;
  permission: number;
  isDataPlate: boolean;
  orientation: number;
  sortOrder: number;
  hasModifiedImage: boolean;
  hasMeasurementImage: boolean;
  status: string;
  usedImageMode: number;
  usedImageModeControl: FormControl<number>;
  lastUpdateDate: string;
  isMeasurementEnabled: boolean;
  bookMarkOption?: BookMarkOption[];
}

export class InspectionImageDisplayRow {
  locationId: string;
  name: string;
  images: InspectionImageListItem[];

  get displayLocationId(): string {
    return this.locationId;
  }

  get displayLocationName(): string {
    return this.name;
  }

  constructor(locationId: string | null, name: string = "Unallocated") {
    this.locationId = locationId;
    this.name = name;
    this.images = [];
  }
}
// AI-UI Evaluation
export class ImagesEvaluationList {
  score: number;
  label: string; // crack,chip,scratch
  location: ImagesEvaluationPoint;
}
export class ImagesEvaluationPoint {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class InspectionImageForOffline {
  id: string;
  isDeleted: boolean;
  isValid: boolean;
  schemaVersion: string;
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;
  inspectionId: string;
  subjectLocationId: string;
  mimeType: string;
  fileName: string;
  filePath: string;
  thumbFilePath: string;
  localFilePath: string;
  imageDescription: string;
  imageType: string;
  datetime: string;
  comment: string;
  scopeModel: string;
  metadata: string;
  vegaMeasurment: string;
  width: number;
  height: number;
  isDataPlate: boolean;
  orientation: number;
  sortOrder: number;
  tenantId: string;
  modifiedItems?: any;
  modifiedHeight: any;
  modifiedWidth: any;
  measurementItems?: any;
  measurementHeight: any;
  measurementWidth: any;
  usedImageMode: number;
  originalMimeType: string;
  status: string;
  bookMarkOption: BookMarkOption[];
  isExistsAudio: boolean;
  audioFilePath: string;

  constructor(schema: InspectionImageSchema) {
    this.id = schema.id;
    this.isDeleted = schema.isDeleted;
    this.isValid = schema.isValid;
    this.schemaVersion = schema.schemaVersion;
    this.createdBy = schema.createdBy;
    this.createDate = schema.createDate;
    this.lastUpdatedBy = schema.lastUpdatedBy;
    this.lastUpdateDate = schema.lastUpdateDate;
    this.inspectionId = schema.inspectionId;
    this.subjectLocationId = schema.subjectLocationId;
    this.mimeType = schema.mimeType;
    this.fileName = schema.fileName;
    this.filePath = schema.filePath;
    this.thumbFilePath = schema.thumbFilePath;
    this.localFilePath = schema.localFilePath;
    this.imageDescription = schema.imageDescription;
    this.imageType = schema.imageType;
    this.datetime = schema.datetime;
    this.comment = schema.comment;
    this.scopeModel = schema.scopeModel;
    this.metadata = schema.metadata;
    this.vegaMeasurment = schema.vegaMeasurement;
    this.isDataPlate = schema.isDataPlate;
    this.orientation = schema.usedImageMode === 0 ? schema.orientation : 1;
    this.width = this.orientation === 6 || this.orientation === 8 ? schema.height : schema.width;
    this.height = this.orientation === 6 || this.orientation === 8 ? schema.width : schema.height;
    this.sortOrder = schema.sortOrder;
    this.tenantId = schema.tenantId;
    this.modifiedItems = JSON.parse(schema.modifiedItems);
    this.modifiedHeight = schema.modifiedHeight;
    this.modifiedWidth = schema.modifiedWidth;
    this.measurementItems = JSON.parse(schema.measurementItems);
    this.measurementHeight = schema.measurementHeight;
    this.measurementWidth = schema.measurementWidth;
    this.usedImageMode = schema.usedImageMode;
    this.originalMimeType = schema.originalMimeType;
    this.status = schema.status;
    this.audioFilePath = schema.audioFilePath;

    if (schema.bookMarkOption) {
      const jsonData = JSON.parse(schema.bookMarkOption);
      this.bookMarkOption = jsonData.map((item: any) => {
        const bookmarkOption = new BookMarkOption();
        bookmarkOption.mtime = item.mtime;
        return bookmarkOption;
      });
    }
    this.isExistsAudio = schema.audioFilePath ? true : false;
  }
}

export class InspectionImageWithUploadUrl extends InspectionImage {
  uploadUrl: string;
}

export class MeasurementInfo {
  measurementCount: number = 0;
  measurementDataList: MeasurementData[] = [];
}

export class MeasurementData {
  measurementType: number;
  references: Vertices2Df[];
  points: Vertices2Df[];
  length: number;
  area: number;
  depth: number;
  objectDistance: number;
  subResult: number;
}

export class InspectionImagesSortOrderForOffline {
  inspectionId: string;
  locationSortOrder: LocationOrder[];

  constructor(inspectionId: string, locationSortOrder: LocationOrder[]) {
    this.inspectionId = inspectionId;
    this.locationSortOrder = locationSortOrder;
  }
}

export class LocationOrder {
  subjectLocationId: string;
  imagesSortOrder: ImagesSortOrder[];

  constructor(subjectLocationId: string, imagesSortOrder: ImagesSortOrder[]) {
    this.subjectLocationId = subjectLocationId;
    this.imagesSortOrder = imagesSortOrder;
  }
}

export class ImagesSortOrder {
  id: string;
  sortOrder: number;

  constructor(id: string, sortOrder: number) {
    this.id = id;
    this.sortOrder = sortOrder;
  }
}

export class SharedFileSasKey {
  dataId: string;
  dataType: string;
  startDate: string;
  expiryDate: string;
  sharedUserMailAddress: string[];
}

/**
 * EXIF Wrapper.exe の戻り値用クラス。
 */
export class ExifWrapperResult {
  imageDescription: string;
  scopeModel: string;
  datetime: string;
  imageType: string;
  orientation: number;
  width: number;
  height: number;
  metadata: string;
  vegaMeasurement: string;
}
